<template>
  <div class="container">
    <h1>カタログサイトのファイル復元</h1>
    <p>
      カタログサイトで削除されたリソースをダウンロードします。
    </p>
    <p class="errorMsg" v-if="errorFlag">
      読み込みに失敗しました。<br />
      時間を置いて再度ご確認ください。
    </p>
    <div class="alert">
      <v-alert
        v-model="alert"
        border="start"
        variant="tonal"
        closable
        close-label=""
        color="red-accent-4"
        title="一致するアイテム無し"
      >
        検索した語句に一致するバックアップが1つもありませんでした。
      </v-alert>
      <v-alert
        v-model="successAlert"
        border="start"
        variant="tonal"
        closable
        close-label=""
        color="green-accent-4"
        title="完了"
      >
        {{ successMessage }}
      </v-alert>
    </div>
    <div class="nonTable">
      <div class="fillterSpase">
        <v-card class="card">
          <div class="cardTitle" @click="show = !show">
            <v-icon icon="mdi-magnify" class="filterIcon"></v-icon>
            <p class="filterTitle">キーワード検索</p>
            <v-icon
              icon="mdi-chevron-down"
              class="arrowIcon"
              v-show="!show"
            ></v-icon>
            <v-icon
              icon="mdi-chevron-up"
              class="arrowIcon"
              v-show="show"
            ></v-icon>
          </div>
          <v-expand-transition class="expand">
            <div v-show="show">
              <v-divider></v-divider>
              <div class="fillter">
                <v-text-field
                  v-model="searchKeyword"
                  label="検索キーワード"
                  class="searchText"
                  @keyup.enter="search()"
                ></v-text-field>
                <v-btn class="btn1 bg-blue-lighten-1" @click="search()"
                  >検索</v-btn
                >
                <v-btn class="btn2 bg-grey-lighten-3" @click="clear()"
                  >クリア</v-btn
                >
                <p class="explane">
                  （リソース名、データセット名、組織名で検索できます。）
                </p>
              </div>
            </div>
          </v-expand-transition>
        </v-card>
      </div>
    </div>
    <div class="table">
      <loading
        :active="true"
        :is-full-page="false"
        :height="50"
        :opacity="0"
        v-if="loading"
      />
      <div class="nowLoading" v-if="loading">
        <p>Now Loading...</p>
      </div>
      <div class="item" v-if="!loading && items.length > 0">
        <v-table density="default">
          <thead>
            <tr>
              <th class="text-left font-weight-bold">データセット名</th>
              <th class="text-left font-weight-bold">リソース名</th>
              <th class="text-left font-weight-bold">元ファイル名</th>
              <th class="text-left font-weight-bold">組織</th>
              <th class="text-left font-weight-bold">操作</th>
              <th class="text-left font-weight-bold">削除日</th>
              <th class="text-left font-weight-bold">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.backup_id">
              <td class="font-weight-medium">{{ item.dataset_name }}</td>
              <td class="font-weight-medium">{{ item.resource_name || item.original_filename }}</td>
              <td class="font-weight-medium">{{ item.original_filename }}</td>
              <td class="font-weight-medium">{{ item.organization_name }}</td>
              <td class="font-weight-medium">
                <v-chip
                  :color="item.action_type === 'delete' ? 'red' : 'orange'"
                  size="small"
                  variant="tonal"
                >
                  {{ item.action_type === 'delete' ? '削除' : '上書き' }}
                </v-chip>
              </td>
              <td class="font-weight-medium">{{ formatDate(item.created_at) }}</td>
              <td class="font-weight-medium actionCell">
                <v-progress-circular
                  v-if="downloadingId === item.backup_id"
                  indeterminate
                  size="20"
                  width="2"
                  color="blue"
                ></v-progress-circular>
                <v-icon
                  v-else
                  icon="mdi-download"
                  @click="downloadItem(item)"
                  class="actionIcon downloadIcon"
                  title="ダウンロード"
                ></v-icon>
                <v-icon
                  icon="mdi-delete-outline"
                  @click="confirmDelete(item)"
                  class="actionIcon deleteIcon"
                  title="バックアップ削除"
                ></v-icon>
              </td>
            </tr>
          </tbody>
        </v-table>
        <div class="pagination">
          <v-btn
            :disabled="lastKeyStack.length === 0"
            @click="prevPage()"
            variant="outlined"
            size="small"
          >
            &lt; 前へ
          </v-btn>
          <v-btn
            :disabled="!hasNextPage"
            @click="nextPage()"
            variant="outlined"
            size="small"
          >
            次へ &gt;
          </v-btn>
        </div>
      </div>
      <div v-if="!loading && items.length === 0 && !alert" class="noData">
        <p>バックアップデータはありません。</p>
      </div>
    </div>

    <!-- 削除確認ダイアログ -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title>バックアップの削除</v-card-title>
        <v-card-text>
          「{{ deleteTarget ? (deleteTarget.resource_name || deleteTarget.original_filename) : '' }}」のバックアップを削除しますか？<br />
          この操作は取り消せません。
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteDialog = false" :disabled="deleting">キャンセル</v-btn>
          <v-btn color="red" @click="executeDelete()" :loading="deleting" :disabled="deleting">削除</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";

const API_URL = process.env.VUE_APP_BACKUP_API_URL || "https://api.dataplatform-yamanashi.jp/resource-backup";

export default {
  data() {
    return {
      errorFlag: false,
      show: false,
      alert: false,
      successAlert: false,
      successMessage: "",
      loading: true,
      searchKeyword: "",
      items: [],
      lastKeyStack: [],
      currentLastKey: null,
      hasNextPage: false,
      deleteDialog: false,
      deleteTarget: null,
      deleting: false,
      downloadingId: null,
    };
  },
  methods: {
    async fetchPage(lastKey) {
      this.loading = true;
      this.errorFlag = false;
      try {
        const params = {
          keyword: this.searchKeyword,
          limit: 20,
        };
        if (lastKey) {
          params.lastKey = JSON.stringify(lastKey);
        }
        const resp = await axios.get(API_URL + "/backups", {
          headers: { Authorization: "Bearer " + this.token },
          params: params,
        });
        this.items = resp.data.items || [];
        this.hasNextPage = !!resp.data.lastKey;
        this.currentLastKey = resp.data.lastKey || null;
      } catch (error) {
        console.log(error);
        this.errorFlag = true;
        this.items = [];
      }
      this.loading = false;
    },
    search() {
      this.alert = false;
      this.lastKeyStack = [];
      this.currentLastKey = null;
      this.fetchPage(null).then(() => {
        if (this.items.length === 0 && this.searchKeyword) {
          this.alert = true;
        }
      });
    },
    clear() {
      this.alert = false;
      this.searchKeyword = "";
      this.lastKeyStack = [];
      this.currentLastKey = null;
      this.fetchPage(null);
    },
    nextPage() {
      this.lastKeyStack.push(this.currentLastKey);
      this.fetchPage(this.currentLastKey);
    },
    prevPage() {
      this.lastKeyStack.pop();
      const prevKey =
        this.lastKeyStack.length > 0
          ? this.lastKeyStack[this.lastKeyStack.length - 1]
          : null;
      this.fetchPage(prevKey);
    },
    async downloadItem(item) {
      this.downloadingId = item.backup_id;
      try {
        const resp = await axios.get(
          API_URL + "/backups/" + item.backup_id + "/download",
          {
            headers: { Authorization: "Bearer " + this.token },
          }
        );
        window.open(resp.data.download_url, "_blank");
      } catch (error) {
        console.log(error);
        this.errorFlag = true;
      }
      this.downloadingId = null;
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async executeDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      const name =
        this.deleteTarget.resource_name ||
        this.deleteTarget.original_filename;
      try {
        await axios.delete(
          API_URL + "/backups/" + this.deleteTarget.backup_id,
          {
            headers: { Authorization: "Bearer " + this.token },
          }
        );
        this.deleteDialog = false;
        this.deleteTarget = null;
        this.successMessage = "「" + name + "」のバックアップを削除しました。";
        this.successAlert = true;
        this.fetchPage(null);
        this.lastKeyStack = [];
      } catch (error) {
        console.log(error);
        this.deleteDialog = false;
        this.errorFlag = true;
      }
      this.deleting = false;
    },
    formatDate(isoString) {
      if (!isoString) return "";
      var d = new Date(isoString);
      var y = d.getFullYear();
      var m = ("0" + (d.getMonth() + 1)).slice(-2);
      var day = ("0" + d.getDate()).slice(-2);
      return y + "/" + m + "/" + day;
    },
  },
  mounted() {
    this.fetchPage(null);
  },
  computed: {
    token() {
      return this.$store.getters.token;
    },
  },
  components: {
    Loading,
  },
};
</script>

<style scoped>
.container {
  width: 100%;
  margin: 36px 5%;
  display: flex;
  flex-direction: column;
  row-gap: 10px;
}

.nowLoading {
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 100px;
}

.item {
  margin-top: 40px;
  width: 90%;
}

.fillterSpase {
  width: 50%;
  margin-bottom: 12px;
}

.card {
  margin-top: 36px;
  padding: 1vw;
}

.cardTitle {
  display: grid;
  grid-template-columns: 30px auto auto 30px;
  grid-template-rows: auto;
  justify-items: start;
  align-items: center;
  grid-template-areas: "filterIcon filterTitle . arrowIcon";
  font-size: 1.2vw;
  cursor: pointer;
}

.filterTitle {
  grid-area: filterTitle;
  font-size: 1.2vw;
  font-weight: 500;
}

.filterIcon {
  grid-area: filterIcon;
}

.arrowIcon {
  grid-area: arrowIcon;
}

.expand {
  padding: 28px 0;
}

.fillter {
  display: grid;
  padding: 2vw;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 0.5fr 0.5fr;
  grid-template-areas:
    "search search"
    "btn1 btn2"
    "explane explane";
  grid-column-gap: 20px;
  grid-row-gap: 1vw;
}

.explane {
  grid-area: explane;
}

.nonTable {
  display: flex;
  width: 100%;
  align-items: flex-start;
  column-gap: 2vw;
}

.table {
  position: relative;
}

.searchText {
  grid-area: search;
}

.btn1 {
  grid-area: btn1;
}

.btn2 {
  grid-area: btn2;
}

.alert {
  width: 80%;
}

.actionCell {
  display: flex;
  align-items: center;
  column-gap: 12px;
}

.actionIcon {
  cursor: pointer;
}

.downloadIcon {
  color: #1976d2;
}

.deleteIcon {
  color: #d32f2f;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 16px;
  margin-top: 24px;
}

.noData {
  margin-top: 40px;
  text-align: center;
  color: #666;
}
</style>
